import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Typography, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Container from 'common/components/Container';
import Spinner from 'common/components/Spinner';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { Routes, push } from 'routing';
import {
	createDiscountCode,
	isUniqueShopDiscountCode,
} from 'views/Products/__utils/creatingDiscountCodeUtils';
import useFormField, { Form, validateForm } from 'common/hooks/useFormField';
import DiscountUsageLimit from '../components/DiscountUsageLimit';
import { useTranslation } from 'services/localization/useTranslation';
import { DiscountCodeApi, PriceRule } from 'common/modules/discountCodes/types';
import { DiscountCodeName } from '../components/DiscountCodeName';
import DiscountType from '../components/DiscountType';
import { validateCode, validateDiscountPriceRule } from '../validations';
import errorHandler from 'common/services/errorHandler';
import DiscountLimits from '../components/DiscountLimits';
import DiscountValidityLimit from '../components/DiscountValidityLimit';
import { DiscountCodeValidity } from '../types';

const noValidate = () => undefined;

type NewDiscountCodeForm = Required<
	Pick<
		DiscountCodeApi,
		| 'code'
		| 'priceRule'
		| 'entitledProductIds'
		| 'entitledCategoryIds'
		| 'usageLimit'
		| 'validFrom'
		| 'validTo'
		| 'bookingStartDateFrom'
		| 'bookingStartDateTo'
	>
>;

const CreateDiscountCode = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const shop = useSelector(ShopSelectors.activeShopData)!;
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);

	const form: Form<NewDiscountCodeForm> = {
		code: useFormField<string>('', (value) => validateCode(value, t)),
		priceRule: useFormField<PriceRule>(
			{
				valueType: 'percentage',
				value: 0,
			},
			(value) => validateDiscountPriceRule(value, t),
		),
		entitledProductIds: useFormField<string[] | null>(null, noValidate),
		entitledCategoryIds: useFormField<string[] | null>(null, noValidate),
		usageLimit: useFormField<number | null>(null, noValidate),
		validFrom: useFormField<string | null>(null, noValidate),
		validTo: useFormField<string | null>(null, noValidate),
		bookingStartDateFrom: useFormField<string | null>(null, noValidate),
		bookingStartDateTo: useFormField<string | null>(null, noValidate),
	};

	const validity: DiscountCodeValidity = {
		validFrom: form.validFrom.value,
		validTo: form.validTo.value,
		bookingStartDateFrom: form.bookingStartDateFrom.value,
		bookingStartDateTo: form.bookingStartDateTo.value,
	};

	const handleSubmit = async () => {
		const { errors, values } = validateForm(form);
		if (errors.length > 0) return;
		setLoading(true);
		try {
			const isValid = await isUniqueShopDiscountCode(values.code, shop.shopInfo.id);
			if (!isValid) {
				setError(
					t(
						'discount.codeWithNameAlreadyExists',
						'A code with this name already exists. Try a new code.',
					),
				);
				setLoading(false);
				return;
			}

			await createDiscountCode({
				code: values.code,
				shopIds: [shop.shopInfo.id],
				priceRule: values.priceRule,
				usageLimit: values.usageLimit,
				usageCount: 0,
				entitledCategoryIds: values.entitledCategoryIds,
				entitledProductIds: values.entitledProductIds,
				validFrom: values.validFrom,
				validTo: values.validTo,
				bookingStartDateFrom: values.bookingStartDateFrom,
				bookingStartDateTo: values.bookingStartDateTo,
			});
			push(Routes.CatalogTab('discounts'));
		} catch (e) {
			errorHandler.report(e);
			setError(e.message);
		}
		setLoading(false);
	};

	const renderSaveButton = () => {
		return (
			<Box className={classes.bottom}>
				{Boolean(error) && (
					<Typography variant="body1" color="error" align="center" gutterBottom>
						{error}
					</Typography>
				)}
				<Button
					fullWidth
					disabled={loading}
					onClick={handleSubmit}
					variant="contained"
					color="primary"
				>
					{loading ? (
						<Spinner padding={8} thickness={4} color="white" />
					) : (
						t('discount.createDiscountCode', 'Create discount code')
					)}
				</Button>
			</Box>
		);
	};

	const handleValidityChange = (validity: DiscountCodeValidity) => {
		form.validFrom.handleChange(validity.validFrom);
		form.validTo.handleChange(validity.validTo);
		form.bookingStartDateFrom.handleChange(validity.bookingStartDateFrom);
		form.bookingStartDateTo.handleChange(validity.bookingStartDateTo);
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('discount.newDiscountCode', 'New discount code')}
				rightContent={renderSaveButton()}
			/>
			<SetPageNavAction
				backAction={{
					to: Routes.CatalogTab('discounts'),
					title: t('discount.discounts', 'Discounts'),
				}}
			/>
			<Container densePaddingTop>
				<DiscountCodeName
					value={form.code.value}
					onChange={form.code.handleChange}
					error={form.code.error}
					autoFocus
				/>
				<DiscountType
					priceRule={form.priceRule.value}
					onChange={form.priceRule.handleChange}
					error={form.priceRule.error}
					currency={shopCurrency}
				/>
				<DiscountLimits
					entitledCategoryIds={form.entitledCategoryIds.value}
					entitledProductIds={form.entitledProductIds.value}
					onCategoryChange={form.entitledCategoryIds.handleChange}
					onProductChange={form.entitledProductIds.handleChange}
				/>
				<DiscountUsageLimit
					limit={form.usageLimit.value}
					onLimitChange={form.usageLimit.handleChange}
				/>
				<DiscountValidityLimit validity={validity} onValidityChange={handleValidityChange} />
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bottom: {
			margin: '0 auto',
			width: '100%',
			maxWidth: '300px',
		},
		bottomMargin: {
			marginBottom: theme.spacing(3),
		},
		marginTop: {
			marginTop: theme.spacing(1),
		},
		section: {
			marginTop: theme.spacing(4),
		},
		discountType: {
			maxWidth: 300,
			margin: theme.spacing(1, 0, 3, 0),
		},
	}),
);

export default CreateDiscountCode;
